import { TaskResultResponse } from "@protos/common/v1/common";
import { authPost } from "@shared/api";
import { useTaskResult } from "@shared/hooks";

import { PatternFixingRequest, PatternFixingTaskStartResponse } from "../types";

/*****************************************************************************/
/* API */
/*****************************************************************************/

const postPatternFixing = async (
  request: PatternFixingRequest
): Promise<PatternFixingTaskStartResponse> => {
  const response = await authPost<
    PatternFixingRequest,
    PatternFixingTaskStartResponse
  >(`/api/ai/pattern_fix`, request);
  return response;
};

/*****************************************************************************/
/* Hook */
/*****************************************************************************/

export interface PostPatternFixingParams {
  request: PatternFixingRequest;
  onSuccess: (data: TaskResultResponse) => void;
}

/**
 * A hook for the pattern fixing API, which ingests non-parsing patterns
 * and uses generative AI to produce related patterns that parse.
 *
 * @param request The request object to be sent to the endpoint.
 * @param onSuccess A callback for when the new pattern is successfully generated.
 */
export const usePostPatternFixing = ({
  request,
  onSuccess,
}: PostPatternFixingParams) =>
  useTaskResult({
    startTaskFn: async () => {
      const { task_token_jwt } = await postPatternFixing(request);
      if (!task_token_jwt) throw new Error("Task token not found");
      return task_token_jwt;
    },
    options: {
      onSuccess: onSuccess,
      onTaskStartError: () => {},
      onTaskResultErrorFromCelery: () => {},
      onTaskResultErrorFromPolling: () => {},
    },
  });
